import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import {
  EVIDENCE_CHART_LOGIN_CHECK_KEY,
  fetchEvidenceChartAnalysis,
} from '../lib/studentEvidenceChartAnalysis'
import toast from 'react-hot-toast'
import { NAVIGATION_PERMISSION_STORAGE_KEY } from '../lib/navigationPolicy'
import { hydrateAcousticCalibrationFromServer } from '../lib/acoustic/acousticCalibrationSync'

interface User {
  id?: number
  username: string
  role: string
  allRoles?: string[]
  roles?: string[]
  displayName?: string
  first_name?: string
  last_name?: string
  mfa_required?: boolean
}

interface LoginCredentials {
  username: string
  password: string
}

interface MfaPendingState {
  methods: string[]
  emailMask?: string | null
}

interface LoginResult {
  mfa_required: boolean
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<LoginResult>
  verifyMfa: (payload: { method: 'totp' | 'email'; code: string; trustDevice?: boolean; deviceLabel?: string }) => Promise<void>
  requestEmailMfaCode: () => Promise<void>
  startPendingTotpSetup: () => Promise<{ qrDataUrl: string }>
  activatePendingTotpSetup: (payload: { code: string; trustDevice?: boolean; deviceLabel?: string }) => Promise<void>
  mfaPending: MfaPendingState | null
  clearMfaPending: () => void
  logout: () => void
  isLoggingIn: boolean
  isLoggingOut: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [mfaPending, setMfaPending] = useState<MfaPendingState | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const AUTH_SYNC_KEY = 'auth:sync'

  const applyAuthenticatedUser = async (loggedInUser: any, successMessage: string) => {
    setMfaPending(null)
    sessionStorage.setItem('user', JSON.stringify(loggedInUser))
    localStorage.removeItem(NAVIGATION_PERMISSION_STORAGE_KEY)
    setUser(loggedInUser)
    setIsAuthenticated(true)
    localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()))

    ;(async () => {
      try {
        const res = await api.get('/auth/me')
        const freshUser = res.data?.data
        if (freshUser) {
          sessionStorage.setItem('user', JSON.stringify(freshUser))
          setUser(freshUser)
          void hydrateAcousticCalibrationFromServer(freshUser)
        }
      } catch {}
    })()

    toast.success(successMessage)
    const primaryRole = loggedInUser?.role?.toLowerCase?.()
    if (primaryRole === 'student') {
      sessionStorage.setItem(EVIDENCE_CHART_LOGIN_CHECK_KEY, '1')
      void queryClient.invalidateQueries({ queryKey: ['student-evidence-chart-analysis'] })
      void queryClient.prefetchQuery({
        queryKey: ['student-evidence-chart-analysis', 'ALL', true],
        queryFn: () => fetchEvidenceChartAnalysis('ALL', true),
      })
    }
    navigate(primaryRole === 'student' ? '/Schüler_Dashboard' : primaryRole === 'parent' ? '/eltern/nachrichten' : '/dashboard')
  }

  const syncAuthState = async (withLoading = false) => {
    const isPublicRoute = window.location.pathname.startsWith('/elternbrief/')
      || window.location.pathname.startsWith('/eltern/einladung/')
    if (isPublicRoute) {
      setIsAuthenticated(false)
      setIsLoading(false)
      setUser(null)
      return
    }

    if (withLoading) {
      setIsLoading(true)
    }

    try {
      const res = await api.get('/auth/me')
      const freshUser = res.data?.data
      if (freshUser) {
        sessionStorage.setItem('user', JSON.stringify(freshUser))
        setUser(freshUser)
        setIsAuthenticated(true)
        void hydrateAcousticCalibrationFromServer(freshUser)
      } else {
        setIsAuthenticated(false)
        setUser(null)
        sessionStorage.removeItem('user')
      }
    } catch {
      // No valid cookie or session expired
      setIsAuthenticated(false)
      setUser(null)
      sessionStorage.removeItem('user')
    } finally {
      setIsLoading(false)
    }
  }

  // Initialize / refresh auth when route changes (e.g. after parent invite accept).
  useEffect(() => {
    const isPublicRoute = location.pathname.startsWith('/elternbrief/')
      || location.pathname.startsWith('/eltern/einladung/')

    if (isPublicRoute) {
      setIsAuthenticated(false)
      setIsLoading(false)
      setUser(null)
      return
    }

    const storedUser = sessionStorage.getItem('user')

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setUser(parsedUser)
        setIsAuthenticated(true)
      } catch {
        sessionStorage.removeItem('user')
      }
    }

    void syncAuthState(false)
  }, [location.pathname])

  // Keep auth state consistent across tabs and on tab focus
  useEffect(() => {
    const syncWithoutSpinner = () => {
      void syncAuthState(false)
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncWithoutSpinner()
      }
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === AUTH_SYNC_KEY) {
        syncWithoutSpinner()
      }
    }

    window.addEventListener('focus', syncWithoutSpinner)
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('focus', syncWithoutSpinner)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const loginMutation = useMutation(
    (credentials: LoginCredentials) =>
      api.post('/auth/login', credentials).then(res => res.data),
    {
      onSuccess: async data => {
        if (data?.mfa_required) {
          setMfaPending({
            methods: Array.isArray(data.methods) ? data.methods : [],
            emailMask: data.email_mask || null
          })
          toast('Bitte zweiten Faktor bestaetigen.', { icon: '🔐' })
          return
        }

        const loggedInUser = data?.data?.user
        if (!loggedInUser) {
          throw new Error('Login response without user payload')
        }

        await applyAuthenticatedUser(loggedInUser, 'Erfolgreich angemeldet')
      },
      onError: (error: any) => {
        const apiError = error?.response?.data?.error
        if (apiError === 'PENDING_APPROVAL') {
          toast.error('Ihr Konto wartet noch auf Freischaltung durch einen Administrator.')
          return
        }
        if (apiError === 'REGISTRATION_REJECTED') {
          toast.error('Ihre Registrierung wurde abgelehnt. Bitte wenden Sie sich an die Schulleitung.')
          return
        }
        toast.error(error.response?.data?.message || 'Anmeldung fehlgeschlagen')
      },
    }
  )

  const verifyMfaMutation = useMutation(
    (payload: { method: 'totp' | 'email'; code: string; trustDevice?: boolean; deviceLabel?: string }) =>
      api.post('/auth/mfa/verify', {
        method: payload.method,
        code: payload.code,
        trust_device: payload.trustDevice ?? true,
        device_label: payload.deviceLabel || undefined
      }).then(res => res.data),
    {
      onSuccess: async (data) => {
        const loggedInUser = data?.data?.user
        if (!loggedInUser) {
          throw new Error('MFA response without user payload')
        }

        await applyAuthenticatedUser(loggedInUser, '2FA erfolgreich bestaetigt')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || '2FA-Pruefung fehlgeschlagen')
      }
    }
  )

  const emailMfaCodeMutation = useMutation(
    () => api.post('/auth/mfa/email/request').then(res => res.data),
    {
      onSuccess: () => {
        toast.success('E-Mail-Code versendet')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'E-Mail-Code konnte nicht versendet werden')
      }
    }
  )

  const pendingTotpSetupMutation = useMutation(
    () => api.post('/auth/mfa/pending/setup/totp').then(res => res.data),
    {
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'QR-Code konnte nicht geladen werden')
      }
    }
  )

  const pendingTotpActivateMutation = useMutation(
    (payload: { code: string; trustDevice?: boolean; deviceLabel?: string }) =>
      api.post('/auth/mfa/pending/activate/totp', {
        code: payload.code,
        trust_device: payload.trustDevice ?? true,
        device_label: payload.deviceLabel || undefined
      }).then(res => res.data),
    {
      onSuccess: async (data) => {
        const loggedInUser = data?.data?.user
        if (!loggedInUser) {
          throw new Error('Pending TOTP activation response without user payload')
        }
        await applyAuthenticatedUser(loggedInUser, 'FreeOTP verbunden und Anmeldung abgeschlossen')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'FreeOTP konnte nicht aktiviert werden')
      }
    }
  )

  const logoutMutation = useMutation(() => api.post('/auth/logout'), {
    onSuccess: () => {
      // Dismiss all existing toasts before logout
      toast.dismiss()
      
      // Cookie wird vom Backend gelöscht
      sessionStorage.removeItem('user')
      sessionStorage.removeItem(EVIDENCE_CHART_LOGIN_CHECK_KEY)
      localStorage.removeItem(NAVIGATION_PERMISSION_STORAGE_KEY)
      setIsAuthenticated(false)
      setUser(null)
      setMfaPending(null)
      localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()))
      toast.success('Erfolgreich abgemeldet', {
        id: 'logout-success',
        duration: 3000
      })
      navigate('/login')
    },
    onError: () => {
      // Cookie wird vom Backend gelöscht (oder ist bereits ungültig)
      sessionStorage.removeItem('user')
      sessionStorage.removeItem(EVIDENCE_CHART_LOGIN_CHECK_KEY)
      localStorage.removeItem(NAVIGATION_PERMISSION_STORAGE_KEY)
      setIsAuthenticated(false)
      setUser(null)
      setMfaPending(null)
      localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()))
      navigate('/login')
    },
  })

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login: async (credentials) => {
      return new Promise((resolve, reject) => {
        loginMutation.mutate(credentials, {
          onSuccess: (data) => resolve({ mfa_required: Boolean(data?.mfa_required) }),
          onError: (error) => reject(error)
        })
      })
    },
    verifyMfa: async (payload) => {
      return new Promise((resolve, reject) => {
        verifyMfaMutation.mutate(payload, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error)
        })
      })
    },
    requestEmailMfaCode: async () => {
      return new Promise((resolve, reject) => {
        emailMfaCodeMutation.mutate(undefined, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error)
        })
      })
    },
    startPendingTotpSetup: async () => {
      return new Promise((resolve, reject) => {
        pendingTotpSetupMutation.mutate(undefined, {
          onSuccess: (data) => resolve({ qrDataUrl: data?.data?.qrDataUrl || '' }),
          onError: (error) => reject(error)
        })
      })
    },
    activatePendingTotpSetup: async (payload) => {
      return new Promise((resolve, reject) => {
        pendingTotpActivateMutation.mutate(payload, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error)
        })
      })
    },
    mfaPending,
    clearMfaPending: () => setMfaPending(null),
    logout: () => logoutMutation.mutate(),
    isLoggingIn:
      loginMutation.isLoading ||
      verifyMfaMutation.isLoading ||
      emailMfaCodeMutation.isLoading ||
      pendingTotpSetupMutation.isLoading ||
      pendingTotpActivateMutation.isLoading,
    isLoggingOut: logoutMutation.isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

